import {
  IonButton,
  IonButtons,
  IonCol,
  IonGrid,
  IonIcon,
  IonInput,
  IonLabel,
  IonList,
  IonRow,
  IonSelect,
  IonSelectOption,
} from '@ionic/react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { chevronDownOutline } from 'ionicons/icons';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import PoiIcon from '@/assets/icons/poi.svg';
import LocationSearchInput from '@/components/PlacesAutocomplete';
import ENVIRONMENT from '@/environments/environment';
import { AnalyticsOrigination, AnalyticsStepNumber } from '@/interfaces/AnalyticsInterface/AnalyticsInterface';
import { CreditProposalUpdate } from '@/interfaces/CreditProposalInterface/CreditProposalInterface';
import {
  Job,
  PostalAddress,
} from '@/interfaces/CustomerInterface/CustomerInterface';
import { DecisionStatus } from '@/interfaces/DecisionInterface/DecisionInterface';
import { EDNIModalRoutes } from '@/interfaces/EDniModalInterface/EDniModalInterface';
import { Cities, KeyModel } from '@/redux/eDniModal/appConfig/appConfig';
import {
  getDecision,
  getLaborStatus,
  getStreetTypes,
  updateProposal,
} from '@/redux/eDniModal/eDniModalAPI';
import {
  getTimeResult, registerLaborDataContinue, registerLoanRefused, registerManualInput,
} from '@/services/analytics';
import { RootState } from '@/store';
import {
  blockCharacters,
  getCity,
  getRoadType,
} from '@/utils/formHelper';
import { VALIDATOR_PATTERN } from '@/utils/validators';

import styles from './LaborData.module.scss';

interface LaborDataProps {
  nextStep: (arg0: EDNIModalRoutes) => void;
  handleLoading: (arg0: boolean) => void;
  errorType?: (arg0: string) => void;
  sendIFrameUrl: (url: string) => void;
}

export default function LaborData({
  nextStep, handleLoading, errorType, sendIFrameUrl,
}: LaborDataProps) {
  const creditProposal = useSelector(
    (state: RootState) => state.creditProposal.data,
  );
  const customerClassification = useSelector(
    (state: RootState) => state.customerClassification.data,
  );
  const [autocompleteAttempts, setAutocompleteAttempts] = useState<number>(0);
  const [autocompleteZeroResults, setAutocompleteZeroResults] = useState<boolean>(false);
  const [manualMode, setManualMode] = useState<boolean>(false);
  const [citiesByPostalCodeList, setCitiesByPostalCodeList] = useState<Cities[]>([]);
  const [initTime] = useState<dayjs.Dayjs>(dayjs());

  const [laborStatusList, setLaborStatusList] = useState<KeyModel[]>([]);
  const [streetTypeList, setStreetTypeList] = useState<KeyModel[]>([]);
  const [incomeError, setIncomeError] = useState<string>('');
  const [postalCodeError, setPostalCodeError] = useState<string>('');
  const [addressData, setAddressData] = useState<PostalAddress>({
    address: '',
    city: '',
    number: '',
    postal_code: '',
    road_type: '',
    province: '',
    aux: '',
  });

  const [showAddress, setShowAddress] = useState<boolean | null>(null);

  const dniData = useSelector((state: RootState) => state.dniData?.data);

  const [laborData, setLaborData] = useState<Job>({
    contract_type: '',
    monthly_income: '',
  });
  const addressErrorDnie = useRef<boolean>(false);
  const validateIncome = (value: string) => {
    // const normalizedValue = value.replace('.', ',');
    const incomePattern = /^(?!0$)\d{1,7}([.,]\d{1,2})?$/;
    if (!value) {
      setIncomeError('Indica tus ingresos');
    } else if (!incomePattern.test(value)) {
      setIncomeError('Formato inválido. Mayor que 0 y máximo 7 dígitos');
    } else if (Number(value) < 1.0) {
      setIncomeError('Los ingresos no pueden ser 0');
    } else {
      setIncomeError('');
    }
    setLaborData({
      ...laborData,
      monthly_income: value,
    });
  };

  const validatePostalCode = (value: string) => {
    const postalCodePattern = VALIDATOR_PATTERN.POSTAL_CODE;
    if (!value) {
      setPostalCodeError('Indica el código postal');
      setAddressData((prevState) => ({
        ...prevState,
        postal_code: '',
      }));
    } else if (!postalCodePattern.test(value)) {
      setPostalCodeError('Código postal inválido');
    } else {
      setPostalCodeError('');
      setAddressData({
        ...addressData,
        postal_code: value,
      });
    }
  };

  const fetchStreetTypes = async () => {
    try {
      const streetType = await getStreetTypes();
      setStreetTypeList(streetType[0].keys);
    } catch (error) {
      nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
      handleLoading(false);
    }
  };

  const fetchCitiesByPostalCode = async (
    postalCode = addressData.postal_code,
  ) => {
    try {
      if (postalCode !== '' && postalCode.length === 5) {
        const cities = await getCity(postalCode);
        if (cities.length > 0) {
          setCitiesByPostalCodeList(cities);
        } else {
          setPostalCodeError('Código postal inválido');
        }
        return cities;
      }
      return [];
    } catch (error: any) {
      nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
      handleLoading(false);
    }
  };

  const validateManualPostalCode = async (value: string) => {
    const postalCodePattern = VALIDATOR_PATTERN.POSTAL_CODE;

    if (!postalCodePattern.test(value)) {
      setPostalCodeError('Código postal inválido');
      setAddressData({
        ...addressData,
        postal_code: value,
      });
    } else if (postalCodePattern.test(value) && value.length === 5) {
      try {
        const cities = await fetchCitiesByPostalCode(value);
        if (cities && cities.length > 0) {
          setAddressData({
            ...addressData,
            city: cities[0].city,
            province: cities[0].province,
            postal_code: value,
          });
          setPostalCodeError('');
        }
      } catch (error) {
        setPostalCodeError('Código postal inválido');
        setAddressData({
          ...addressData,
          postal_code: value,
        });
      }
    } else {
      setAddressData({
        ...addressData,
        postal_code: value,
      });
      setPostalCodeError('Código postal inválido');
    }
  };

  const getLaborData = async () => {
    try {
      const laborStatus = await getLaborStatus();
      setLaborStatusList(laborStatus[0].keys);
    } catch (error) {
      nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
      handleLoading(false);
    }
  };

  useEffect(() => {
    getLaborData();
    // handleLoading(false);
  }, []);

  useEffect(() => {
    if (manualMode) {
      fetchStreetTypes();
      fetchCitiesByPostalCode();
    }
  }, [manualMode]);
  useEffect(() => {
    if (dniData?.address && dniData?.address !== 'UNKNOWN' && showAddress === null) {
      if (customerClassification?.require_address_data && (showAddress === null || showAddress)
        && (manualMode === false)) {
        handleLoading(true);
      }
    }
  }, [dniData, showAddress]);
  const handleAutocompleteSelect = (
    addressData: PostalAddress,
    completeAddress?: boolean,
  ) => {
    if (!addressErrorDnie.current && dniData?.address === 'UNKNOWN') {
      addressErrorDnie.current = true;
    }

    if (
      dniData?.address && dniData.address !== 'UNKNOWN' && !addressErrorDnie.current
    ) {
      // Si la dirección es válida y no es 'UNKNOWN', y nunca fue 'UNKNOWN'
      if (
        customerClassification?.require_address_data
        && (showAddress === null || showAddress) && manualMode === false
      ) {
        handleLoading(true);
        setShowAddress(false); // Oculta el campo de dirección
      }
    } else if (dniData?.address === 'UNKNOWN') {
      setShowAddress(true); // Muestra el campo de dirección
    } else {
      setShowAddress(false); // Oculta el campo de dirección
    }

    if (addressData.postal_code === '' || addressData.number === '') {
      setAutocompleteAttempts(autocompleteAttempts + 1);
    }
    setAddressData(addressData);
    if (completeAddress && !addressErrorDnie.current) {
      setShowAddress(false);
      if (!customerClassification?.require_labor_data) {
        handleSubmit(addressData);
      } else {
        handleLoading(false);
      }
    } else {
      setShowAddress(true);
      handleLoading(false);
    }

    handleLoading(false);
  };
  const handleSubmit = async (addressAutocompleted?: PostalAddress) => {
    try {
      if (!creditProposal) return console.error('proposal not found');
      const proposalUpdateDate: CreditProposalUpdate[] = [];
      if (customerClassification?.require_address_data) {
        const formattedAddress = addressAutocompleted || addressData;
        if (addressData.address.length > 46) {
          formattedAddress.address = addressData.address.substring(0, 46);
          formattedAddress.aux = addressData.address.substring(46, 66);
        }
        if (manualMode && !addressAutocompleted && formattedAddress.road_type.length > 2) {
          const roadTypeValue = getRoadType(formattedAddress.road_type);
          formattedAddress.road_type = roadTypeValue;
        }
        const proposalAddressData: CreditProposalUpdate = {
          op: 'replace',
          path: 'customer/address',
          value: formattedAddress,
        };
        proposalUpdateDate.push(proposalAddressData);
      }
      let proposalJobData: CreditProposalUpdate;
      if (customerClassification?.require_labor_data) {
        proposalJobData = {
          op: 'replace',
          path: 'customer/job',
          value: laborData,
        };
        proposalUpdateDate.push(proposalJobData);
      }
      handleLoading(true);
      const proposalUpdated = await updateProposal(
        creditProposal.credit_proposal_id,
        proposalUpdateDate,
      );
      const laborDataAnalytics = {
        time_elapsed: getTimeResult(initTime),
        step_number: AnalyticsStepNumber.LaborData,
        cust_contract_type: laborData?.contract_type || '',
        cust_monthly_income: laborData?.monthly_income || '',
        cust_province: proposalUpdated?.customer?.address?.postal_code?.slice(0, 2) || '',
        cust_postal_code: proposalUpdated?.customer?.address?.postal_code || '',
        used_manual_input: manualMode || false,
      };
      registerLaborDataContinue({ origin: AnalyticsOrigination.App, uuid: creditProposal.credit_proposal_id }, laborDataAnalytics);
      await getDecisionData(creditProposal.credit_proposal_id, 0);
    } catch (error: any) {
      handleLoading(false);
      if (error?.result?.errors) {
        if (error.result.errors.find((er: any) => er.code === 'DRF0089')) {
          errorType && errorType('DRF0089');
          nextStep(EDNIModalRoutes.PERSONAL_FINANCES_UNAVAILABLE);
        } else if (
          error.result.errors.find((er: any) => er.code === 'DRF0092')
        ) {
          errorType && errorType('DRF0092');
          nextStep(EDNIModalRoutes.PERSONAL_FINANCES_UNAVAILABLE);
        } else {
          nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
        }
      }
    }
  };

  const getDecisionData = async (creditProposalId: string, counter: number) => {
    counter++;
    try {
      handleLoading(true);
      const leadToken = sessionStorage.getItem('leadToken');
      const decisionResp = await getDecision(creditProposalId);
      if (decisionResp) {
        switch (decisionResp.decision) {
          case DecisionStatus.APPROVED:
            nextStep(EDNIModalRoutes.OPERATION_SHIPPING);
            handleLoading(false);
            break;
          case DecisionStatus.DENIED:
            registerLoanRefused({ origin: AnalyticsOrigination.App, uuid: creditProposal?.credit_proposal_id }, 'DEC');
            nextStep(EDNIModalRoutes.DENIAL);
            handleLoading(false);
            break;
          case DecisionStatus.MISSING_DATA:
            nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
            handleLoading(false);
            break;
          case DecisionStatus.COUNTER_OFFER:
            const continuationUrl = `${ENVIRONMENT.URL_PEPPERONE}/?lead_token=${leadToken}&credit_proposal_id=${creditProposalId}&super_express=true&is_app=true`;
            handleLoading(true);
            sendIFrameUrl(continuationUrl);
            break;
          default:
            nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
            handleLoading(false);
            break;
        }
      }
    } catch (error: any) {
      if (error.result.http_code === 409 && counter <= 11) {
        setTimeout(() => {
          getDecisionData(creditProposalId, counter);
        }, 3000);
      } else {
        nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
        handleLoading(false);
      }
    }
  };

  const isDisabled = (): boolean => {
    const hasAddressErrors = Boolean(postalCodeError);
    const hasIncomeErrors = Boolean(incomeError);
    const hasEmptyRequiredFields = (customerClassification?.require_address_data
      && (!addressData.postal_code || !addressData.number))
      || (customerClassification?.require_labor_data
        && (!laborData.contract_type || !laborData.monthly_income));
    return (
      hasAddressErrors
      || hasIncomeErrors
      || hasEmptyRequiredFields
      || (autocompleteZeroResults && !manualMode)
    );
  };

  return (
    <div className={styles.modalLaborDataContainer}>
      {customerClassification?.require_address_data
        && (showAddress === null || showAddress)
        && (manualMode === false ? (
          <div className={styles.laborDataForm}>
            <IonLabel className={styles.modalTitle}>Dirección</IonLabel>
            <IonGrid className={styles.modalFormWrapper}>
              <IonRow className={styles.modalFormRow}>
                <IonCol>
                  <LocationSearchInput
                    onZeroResults={(value) => {
                      setAutocompleteZeroResults(value);
                    }}
                    defaultValue={
                      dniData?.address === 'UNKNOWN' ? '' : dniData?.address
                    }
                    onSelect={handleAutocompleteSelect}
                  />
                </IonCol>
              </IonRow>
              <IonRow className={styles.modalFormRow}>
                <IonCol size="2">
                  <IonInput
                    label="Nº"
                    type="tel"
                    labelPlacement="stacked"
                    className={styles.lowerLeftInput}
                    value={addressData.number}
                    onIonInput={(e: any) => {
                      const number = e.target.value;
                      setAddressData({
                        ...addressData,
                        number,
                      });
                    }}
                    onKeyDown={blockCharacters}
                  />
                </IonCol>
                <IonCol size="4">
                  <IonInput
                    type="tel"
                    maxlength={5}
                    label="C.P."
                    labelPlacement="stacked"
                    value={addressData.postal_code}
                    className={classNames(postalCodeError && 'ion-invalid ', postalCodeError === '' && 'ion-valid')}
                    errorText={postalCodeError}
                    onIonInput={(e: any) => validatePostalCode(e.target.value)}
                    onKeyDown={blockCharacters}
                  />
                  {postalCodeError && <span className={styles.validationError}>{postalCodeError}</span>}
                </IonCol>
                <IonCol size="6">
                  <IonInput
                    label="Escalera, Portal, Piso..."
                    labelPlacement="stacked"
                    onIonInput={(e: any) => setAddressData({
                      ...addressData,
                      aux: e.target.value,
                    })}
                    value={addressData.aux}
                    className={styles.lowerRightInput}
                  />
                </IonCol>
              </IonRow>
            </IonGrid>
            {(autocompleteAttempts > 1 || autocompleteZeroResults) && (
              <div
                className={styles.manualModeWrapper}
                onClick={() => {
                  setManualMode(true);
                  registerManualInput({ origin: AnalyticsOrigination.App, uuid: creditProposal?.credit_proposal_id });
                }}
              >
                <IonIcon icon={PoiIcon} />
                <p>Introducir dirección manualmente</p>
              </div>
            )}
          </div>
        ) : (
          <div className={styles.laborDataForm}>
            <IonLabel className={styles.modalTitle}>Dirección</IonLabel>
            <IonGrid className={styles.modalFormWrapper}>
              <IonRow className={styles.modalFormRow}>
                <IonCol size="4">
                  <IonList lines="full">
                    <IonSelect
                      label="Tipo de vía"
                      labelPlacement="stacked"
                      className={classNames(styles.entryProductInput, styles.upperLeftInput)}
                      value={addressData.road_type}
                      interface="popover"
                      onIonChange={(e: any) => setAddressData({
                        ...addressData,
                        road_type: e.target.value,
                      })}
                      toggleIcon={chevronDownOutline}
                    >
                      {streetTypeList.map((status: KeyModel) => (
                        <IonSelectOption
                          key={status.code}
                          className={styles.laborEntryProductOption}
                        >
                          {status.description_code}
                        </IonSelectOption>
                      ))}
                    </IonSelect>
                  </IonList>
                </IonCol>
                <IonCol size="8">
                  <IonInput
                    label="Dirección"
                    labelPlacement="stacked"
                    value={addressData.address}
                    className={styles.upperRightInput}
                    onIonInput={(e: any) => setAddressData({
                      ...addressData,
                      address: e.target.value,
                    })}
                  />
                </IonCol>
              </IonRow>
              <IonRow className={styles.modalFormRow}>
                <IonCol size="4">
                  <IonInput
                    label="Nº"
                    type="tel"
                    maxlength={5}
                    labelPlacement="stacked"
                    value={addressData.number}
                    onIonInput={(e: any) => setAddressData({
                      ...addressData,
                      number: e.target.value,
                    })}
                  />
                </IonCol>

                <IonCol size="8">
                  <IonInput
                    label="Escalera, Portal, Piso..."
                    labelPlacement="stacked"
                    value={addressData.aux}
                    onIonInput={(e: any) => setAddressData({
                      ...addressData,
                      aux: e.target.value,
                    })}
                  />
                </IonCol>
              </IonRow>

              <IonRow className={styles.modalFormRow}>
                <IonCol>
                  <IonInput
                    label="C.P."
                    type="tel"
                    maxlength={5}
                    pattern="[0-9]*"
                    labelPlacement="stacked"
                    value={addressData.postal_code}
                    className={classNames(postalCodeError && 'ion-invalid ', postalCodeError === '' && 'ion-valid')}
                    errorText={postalCodeError}
                    onIonInput={(e: any) => validateManualPostalCode(e.target.value)}
                    onKeyDown={blockCharacters}
                  />
                  {postalCodeError && <span className="validation-error" />}
                </IonCol>
              </IonRow>

              <IonRow className={styles.modalFormRow}>
                <IonCol>
                  <IonList lines="full">
                    <IonSelect
                      label="Población"
                      labelPlacement="stacked"
                      className={styles.entryProductInput}
                      value={addressData.city}
                      interface="popover"
                      onIonChange={(e: any) => setAddressData({
                        ...addressData,
                        city: e.target.value,
                      })}
                      toggleIcon={chevronDownOutline}
                    >
                      {citiesByPostalCodeList.map((status: any) => (
                        <IonSelectOption
                          key={status.city}
                          className={styles.entryProductOption}
                        >
                          {status.city}
                        </IonSelectOption>
                      ))}
                    </IonSelect>
                  </IonList>
                </IonCol>
              </IonRow>
              <IonRow className={styles.modalFormRow}>
                <IonCol>
                  <IonInput
                    label="Provincia"
                    labelPlacement="stacked"
                    className={classNames(styles.lowerInput, styles.disabledInput)}
                    value={addressData.province}
                    disabled
                  />
                </IonCol>
              </IonRow>
            </IonGrid>
          </div>
        ))}
      {customerClassification?.require_labor_data && (
        <div className={styles.laborDataForm}>
          <IonLabel className={styles.modalTitle}>Datos laborales</IonLabel>
          <IonGrid className={styles.modalFormWrapper}>
            <IonRow className={styles.modalFormRow}>
              <IonCol>
                <IonList lines="full">
                  <IonSelect
                    label="Situación laboral"
                    labelPlacement="stacked"
                    className={classNames(styles.entryProductInput, styles.upperInput)}
                    value={laborData.contract_type}
                    interface="popover"
                    onIonChange={(e: any) => {
                      setLaborData({
                        ...laborData,
                        contract_type: e.target.value,
                      });
                    }}
                    toggleIcon={chevronDownOutline}
                  >
                    <IonSelectOption value="" disabled>
                      SELECCIONAR
                    </IonSelectOption>
                    {laborStatusList.map((status: KeyModel) => (
                      <IonSelectOption
                        value={status.code}
                        key={status.code}
                        className={styles.entryProductOption}
                      >
                        {status.description_code}
                      </IonSelectOption>
                    ))}
                  </IonSelect>
                </IonList>
              </IonCol>
            </IonRow>

            <IonRow className={styles.modalFormRow}>
              <IonCol>
                <IonInput
                  type="number"
                  maxlength={7}
                  label="Ingresos netos mensuales"
                  labelPlacement="floating"
                  className={classNames(styles.lowerInput, incomeError && 'ion-invalid ', incomeError === '' && 'ion-valid')}
                  errorText={incomeError}
                  value={laborData.monthly_income}
                  onIonInput={(e: any) => validateIncome(e.target.value)}
                  onKeyDown={blockCharacters}
                  min={1.0}
                />
                {incomeError && <span className={styles.validationError}>{incomeError}</span>}
              </IonCol>
              {laborData.contract_type === 'C' && (
                <IonLabel className={styles.contractTypeLabel}>
                  *Añade la fuente de ingresos de la unidad familiar
                </IonLabel>
              )}
            </IonRow>
          </IonGrid>
        </div>
      )}
      <IonButtons className={styles.nextBtnWrapper}>
        <IonButton
          className="nextButton"
          onClick={() => handleSubmit()}
          disabled={isDisabled()}
        >
          Continuar
        </IonButton>
      </IonButtons>
    </div>
  );
}
